/**
 * Map Chips Plugin for Native Interactive Site Map
 *
 * Orchestrates the 4 map chip buttons (Available, Model, Coming Soon, Sold): creates the
 * component (which builds its own root element), inserts it
 * into the header, wires click handling, and keeps the active-chip highlight and badge counts
 * in sync with the store.
 *
 * Reason to change: this file should only change when WIRING changes — where the chip bar
 * mounts in the DOM, which store slices/selectors/actions drive it, or plugin lifecycle
 * (start/stop) concerns. It has no opinion on chip visuals, DOM structure, or chip
 * definitions/labels — those live in components/mapChips.dom.js, components/mapChip.dom.js,
 * and services/mapChipService.js respectively, and change independently of this file.
 *
 * @module nativeInteractiveSiteMap/plugins/mapChips.plugin
 */
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import createMapChipsDom from '../components/mapChips.dom';
import createMapChipHandlers from '../handlers/mapChipHandlers';
import { deriveChipByHierarchy } from '../services/mapChipService';
import { selectActiveChip, selectChipLotCounts, selectIsLotInventorySettled } from '../redux/selectors';

export default function createMapChipsPlugin() {
  const state = {
    component: null,
    store$: null,
    dispatch: null,
    dataActions: null,
    subscriptions: [],
  };

  /**
   * Subscribes the component to store changes for active chip highlight and badge counts/
   * visibility. The counts stream only starts emitting once the lot-inventory fetch has settled
   * (see redux/selectors.js#selectIsLotInventorySettled), so chips reveal/hide once, from real
   * data, instead of flashing empty during the load window; from then on the same subscription
   * re-evaluates on every subsequent count change (e.g. future filters), with no separate code
   * path.
   * @returns {import('rxjs').Subscription[]}
   */
  function setupSubscriptions() {
    /**
     * Reveals/hides chips per their count and falls back off a now-empty active chip. Runs both
     * for the initial reveal and every later count change — see setupSubscriptions doc above.
     * @param {{ counts: Record<string, number>, activeChip: string }} params
     */
    function onChipCountsChanged({ counts, activeChip }) {
      state.component?.updateCounts(counts);

      // The active chip may have nothing assigned (e.g. a deep link into a status this
      // community has no Floor Plans/Specs for, or an invalid/unknown lotStatus value not
      // present in counts at all) — fall back to the chip the business hierarchy picks (see
      // services/mapChipService.js#deriveChipByHierarchy).
      if ((counts[activeChip] ?? 0) === 0) {
        const hierarchyChip = deriveChipByHierarchy(counts);
        if (hierarchyChip) {
          state.dispatch(state.dataActions.chipSelected(hierarchyChip.key));
        }
      }
    }

    return [
      state.store$.pipe(
        map(selectActiveChip),
        distinctUntilChanged(),
      ).subscribe((activeChip) => state.component?.setActiveTab(activeChip)),

      state.store$.pipe(
        // Wrapped so rxjs's (value, index) filter signature doesn't pass `index` through to
        // the reselect selector, which would defeat its memoization.
        filter((storeState) => selectIsLotInventorySettled(storeState)),
        map((storeState) => ({
          counts: selectChipLotCounts(storeState),
          activeChip: selectActiveChip(storeState),
        })),
        distinctUntilChanged((previous, next) => (
          previous.counts === next.counts && previous.activeChip === next.activeChip
        )),
      ).subscribe(onChipCountsChanged),
    ];
  }

  return {
    name: 'map-chips',

    async start(context) {
      const header = context.root.querySelector('[data-header]');
      if (!header) {
        return;
      }

      const { getState, dispatch, dataActions, store$ } = context.services;
      state.store$ = store$;
      state.dispatch = dispatch;
      state.dataActions = dataActions;
      const initialState = getState();

      state.component = createMapChipsDom(
        selectChipLotCounts(initialState),
        selectActiveChip(initialState),
      );
      header.appendChild(state.component.getElement());

      const handlers = createMapChipHandlers(
        (chipKey) => dispatch(dataActions.chipSelected(chipKey)),
      );
      state.component.attachEventListeners(handlers);

      state.subscriptions = setupSubscriptions();
    },

    async stop() {
      state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      state.subscriptions = [];
      state.component?.remove();
      state.component = null;
      state.store$ = null;
      state.dispatch = null;
      state.dataActions = null;
    },
  };
}
